// Runtime survey-template loader.
// Templates are served by the API from data/survey-templates.json on the server,
// and fall back to the bundled defaults if the API is unreachable.

import { DEFAULT_SURVEY_TEMPLATES, type SurveyTemplate } from "./survey-defaults";

export type { SurveyTemplate } from "./survey-defaults";

let cached: SurveyTemplate[] | null = null;
let inflight: Promise<SurveyTemplate[]> | null = null;

export async function loadSurveyTemplates(force = false): Promise<SurveyTemplate[]> {
  if (cached && !force) return cached;
  if (inflight && !force) return inflight;

  inflight = (async () => {
    try {
      const res = await fetch("/api/survey-templates", { cache: "no-store" });
      if (res.ok) {
        const data = (await res.json()) as SurveyTemplate[];
        cached = data;
        return data;
      }
    } catch (err) {
      console.warn("Failed to load survey templates from API, using defaults:", err);
    }
    cached = DEFAULT_SURVEY_TEMPLATES;
    return DEFAULT_SURVEY_TEMPLATES;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export function getTemplate(templates: SurveyTemplate[], id: string): SurveyTemplate | undefined {
  return templates.find((t) => t.id === id);
}

export async function saveSurveyTemplates(
  templates: SurveyTemplate[],
  matrixAccessToken: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch("/api/survey-templates", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${matrixAccessToken}`,
      },
      body: JSON.stringify(templates),
    });
    if (res.ok) {
      cached = templates;
      return { ok: true };
    }
    const errBody = await res.text();
    return { ok: false, error: `${res.status}: ${errBody.slice(0, 200)}` };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Network error" };
  }
}
